import * as React from 'react';
import type { AttendanceDay } from '@/lib/hr-api';

interface Props {
  /** Days from API, in any order. Will be aligned to a calendar window. */
  days: AttendanceDay[];
  /** How many trailing calendar days to render. Default 30. */
  windowSize?: number;
  /** Working-shift bounds in minutes-since-midnight, used to scale the band y-axis. */
  shiftStartMin?: number;
  shiftEndMin?: number;
}

/**
 * Bar-chart showing daily presence over a trailing calendar window:
 *   - X axis: days, oldest left, today right.
 *   - Y axis: time-of-day, scaled to [shiftStartMin .. shiftEndMin].
 *   - Each present day = a vertical band from first punch to last punch.
 *   - Absent days = a faint outlined slot (so gaps are visible).
 *
 * No external chart lib — single inline SVG. Hover shows tooltip via <title>.
 */
export function AttendanceChart({
  days,
  windowSize = 30,
  shiftStartMin = 9 * 60,   // 09:00 default frame
  shiftEndMin   = 22 * 60,  // 22:00 default frame
}: Props) {
  const window = React.useMemo(() => buildWindow(days, windowSize), [days, windowSize]);

  const W = 720;
  const H = 160;
  const padL = 36, padR = 8, padT = 10, padB = 22;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const barW = innerW / window.length;

  // Y axis: shift bounds, but auto-expand if any punch falls outside.
  const allFirsts = window.flatMap((d) => (d.entry == null ? [] : [d.entry.firstMin]));
  const allLasts  = window.flatMap((d) => (d.entry == null ? [] : [d.entry.lastMin]));
  const yMin = Math.min(shiftStartMin, ...allFirsts, shiftStartMin);
  const yMax = Math.max(shiftEndMin,   ...allLasts,  shiftEndMin);
  const yRange = Math.max(60, yMax - yMin);

  const yPos = (m: number) => padT + ((m - yMin) / yRange) * innerH;

  const ticks = ticksBetween(yMin, yMax);
  const today = window[window.length - 1]?.iso;

  const presentDays = window.filter((d) => d.entry != null).length;

  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between text-xs text-muted-foreground">
        <span>Last {window.length} days · {presentDays} present</span>
        <span>{firstISO(window)} → {today ?? ''}</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="block w-full" role="img" aria-label="Attendance chart">
        {/* y-axis tick lines + labels */}
        {ticks.map((m) => (
          <g key={m}>
            <line x1={padL} x2={W - padR} y1={yPos(m)} y2={yPos(m)} className="stroke-muted" strokeWidth={1} />
            <text x={padL - 4} y={yPos(m) + 3} textAnchor="end" className="fill-muted-foreground text-[10px]">
              {minToHM(m)}
            </text>
          </g>
        ))}
        {/* bars */}
        {window.map((d, i) => {
          const x = padL + i * barW + barW * 0.15;
          const w = barW * 0.7;
          if (d.entry == null) {
            // absent: empty outline at full height for visual continuity
            return (
              <rect
                key={d.iso}
                x={x} y={padT} width={w} height={innerH}
                className="fill-transparent stroke-muted"
                strokeWidth={1}
                strokeDasharray="2 3"
              >
                <title>{`${d.iso}: absent`}</title>
              </rect>
            );
          }
          const y1 = yPos(d.entry.firstMin);
          const y2 = yPos(d.entry.lastMin);
          const h = Math.max(2, y2 - y1);
          return (
            <rect
              key={d.iso}
              x={x} y={y1} width={w} height={h}
              rx={1.5}
              className={d.entry.firstMin > shiftStartMin + 15
                ? 'fill-amber-500/80'
                : 'fill-emerald-500/80'}
            >
              <title>{
                `${d.iso}\n` +
                `${minToHM(d.entry.firstMin)}–${minToHM(d.entry.lastMin)} ` +
                `(${d.entry.entries} punch${d.entry.entries === 1 ? '' : 'es'})`
              }</title>
            </rect>
          );
        })}
        {/* x-axis day-of-month ticks (every 5 days, plus first + last) */}
        {window.map((d, i) => {
          const labelable = i === 0 || i === window.length - 1 || i % 5 === 0;
          if (!labelable) return null;
          const x = padL + i * barW + barW / 2;
          return (
            <text key={d.iso} x={x} y={H - 6} textAnchor="middle" className="fill-muted-foreground text-[10px]">
              {dayOfMonth(d.iso)}
            </text>
          );
        })}
      </svg>
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <Legend swatch="bg-emerald-500/80" label="On time" />
        <Legend swatch="bg-amber-500/80" label="Late" />
        <Legend swatch="border border-muted bg-transparent" label="Absent" dashed />
      </div>
    </div>
  );
}

function Legend({ swatch, label, dashed }: { swatch: string; label: string; dashed?: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`inline-block h-2.5 w-3 rounded-[1px] ${swatch} ${dashed ? 'border-dashed' : ''}`} />
      {label}
    </span>
  );
}

interface CalendarSlot {
  iso: string;
  entry: { firstMin: number; lastMin: number; entries: number } | null;
}

function buildWindow(days: AttendanceDay[], n: number): CalendarSlot[] {
  // Newest day reported by the API anchors the window — keeps the chart
  // useful even for discontinued employees whose data ends months ago.
  const sorted = [...days].sort((a, b) => (a.date < b.date ? -1 : 1));
  const anchor = sorted[sorted.length - 1]?.date ?? todayISO();
  const byDate = new Map(days.map((d) => [d.date, d]));
  const out: CalendarSlot[] = [];
  for (let i = n - 1; i >= 0; i--) {
    const iso = addDaysISO(anchor, -i);
    const d = byDate.get(iso);
    out.push({
      iso,
      entry: d
        ? { firstMin: hmToMin(d.firstAt), lastMin: hmToMin(d.lastAt), entries: d.entries }
        : null,
    });
  }
  return out;
}

function ticksBetween(min: number, max: number): number[] {
  const out: number[] = [];
  // Hourly ticks rounded to a sensible interval (every 2h if range is wide).
  const step = max - min > 8 * 60 ? 120 : 60;
  const start = Math.ceil(min / step) * step;
  for (let m = start; m <= max; m += step) out.push(m);
  return out;
}

function minToHM(m: number) {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}
function hmToMin(s: string) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s);
  if (!m) return 0;
  return Number(m[1]) * 60 + Number(m[2]);
}
function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function addDaysISO(iso: string, n: number) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y!, m! - 1, d! + n);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}
function dayOfMonth(iso: string) {
  return iso.slice(8);
}
function firstISO(slots: CalendarSlot[]) {
  return slots[0]?.iso ?? '';
}
